import { RetryBankingPaymentDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useBankingAuthorization } from './useBankingAuthorization'

const MISSING_AUTHORIZATION_ERROR = 'Missing authorization'

type RetryPaymentParams = {
  paymentId: number
  employeeId: number
  redirectUrl: string
  transferIds?: number[]
  refetchQueries?: Parameters<typeof useMutation>[1]['refetchQueries']
}

export const useRetryBankingPayment = (
  options?: Pick<Parameters<typeof useMutation>[1], 'refetchQueries'>
) => {
  const [retryBankingPayment, { loading }] = useMutation(RetryBankingPaymentDocument, {
    extractorFn: (data) => data.bankingMutations.retryBankingPayment,
    refetchQueries: options?.refetchQueries,
    awaitRefetchQueries: true,
  })
  const { getAuthorizationUrl, loading: authLoading } = useBankingAuthorization()

  const retryPayment = async ({
    paymentId,
    employeeId,
    redirectUrl,
    transferIds,
  }: RetryPaymentParams) => {
    const result = await retryBankingPayment({
      variables: {
        id: paymentId,
        payerId: employeeId,
        transferIds,
      },
    })
    const handled = handleResult(result, () => {})

    if (handled.success) {
      // Get consentUrl from the last payment execution
      const paymentExecutions = handled.data.payment?.paymentExecutions ?? []
      const lastExecution = paymentExecutions[paymentExecutions.length - 1]
      const consentUrl = lastExecution?.consent?.consentUrl || undefined

      // Only show success flash if no redirect is needed
      if (!consentUrl) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.bulkPayments.detail.retryModal.success'),
        })
      }

      const result = {
        ...handled,
        consentUrl,
        authorizationUrl: undefined,
      }
      return result
    } else {
      // Check if error is due to missing authorization
      const isMissingAuthorization = handled.errors.some((error) => {
        return (
          'messages' in error &&
          error.messages?.some((message) => message.includes(MISSING_AUTHORIZATION_ERROR))
        )
      })

      if (isMissingAuthorization) {
        // Get authorization URL for the user to authorize with Swan
        const authorizationUrl = await getAuthorizationUrl(employeeId, redirectUrl)

        if (authorizationUrl) {
          return {
            ...handled,
            consentUrl: undefined,
            authorizationUrl,
          }
        }
      }

      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
      return {
        ...handled,
        consentUrl: undefined,
        authorizationUrl: undefined,
      }
    }
  }

  return {
    retryPayment,
    loading: loading || authLoading,
  }
}
